import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '../API/api';
import { BASE_URL2 } from '../API/api2'; // ajuste o caminho
import '../styles/empreendimentosStyle/DetalhesEmpreendimento.css';
import { Ruler, ShieldCheck, Building2, TreePalm, Home, Layers, Briefcase, Car, Download } from 'lucide-react';
import Slider from "react-slick";
import Sidebar from '../components/SideBar';
import Map, { Marker } from 'react-map-gl';
import Loading from "../components/Loading";

/* --- TIPOS --- */
type Unidade = {
  id: string;
  numero: string;
  valor: string;
  area: number;
  vagas: number;
  andar: number;
  status: number;
  torre?: string | number;
  atributosExtras?: { nome: string; valor: string | number }[];
};

interface Torre {
  nome: string;
  andares: number;
  unidadesPorAndar: Unidade[];
}

interface Empreendimento {
  id: string;
  titulo: string;
  bairro: string;
  cidade: string;
  preco: string;
  fotos: string[];
  area: number;
  vagas: number;
  caracteristicas: {
    lazer: string[];
    seguranca: string[];
    infraestrutura: string[];
  };
  construtora: string;
  unidadesDisponiveis: number;
  faseObra: string;
  valorMedio: string;
  torres: Torre[];
  unidadesAvulsas: Unidade[];
  latitude: number;
  longitude: number;
  tabela_preco: string;
}

/* --- MODAL --- */
function UnidadeModal({ unidade, onClose }: { unidade: Unidade | null; onClose: () => void }) {

  if (!unidade) return null;

  const statusMap: Record<Unidade["status"], { label: string; color: string }> = {
    1: { label: "Disponível", color: "#44c3ff" },
    2: { label: "Vendido", color: "#ff4d4f" },
  };
  const status = statusMap[unidade.status];

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <div className="modal-header">
          <h2>Unidade {unidade.numero}</h2>
          <span className="mapa-status-pill" style={{ backgroundColor: status.color }}>
            {status.label}
          </span>
        </div>
        <div className="info-cards-unidade">
          <div className="info-card-unidade">
            <Home size={18} />
            <div>
              <span className="info-label-unidade">Valor</span>
              <span className="info-value-unidade">
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(Number(unidade.valor))}
              </span>
            </div>
          </div>
          <div className="info-card-unidade">
            <Ruler size={18} />
            <div>
              <span className="info-label-unidade">Área</span>
              <span className="info-value-unidade">{unidade.area} m²</span>
            </div>
          </div>
          <div className="info-card-unidade">
            <Car size={18} />
            <div>
              <span className="info-label-unidade">Vagas</span>
              <span className="info-value-unidade">{unidade.vagas}</span>
            </div>
          </div>
        </div>
        <hr className="modal-divider" />
        {Array.isArray(unidade.atributosExtras) && unidade.atributosExtras.length > 0 && (
          <div className="atributos-extras">
            {unidade.atributosExtras.map((attr, idx) => (
              <p key={idx}>
                <strong>{attr.nome}:</strong> {attr.valor}
              </p>
            ))}
          </div>
        )}
        <button onClick={onClose} className="btn-fechar">Fechar</button>
      </div>
    </div>
  );
}

/* --- COMPONENTE PRINCIPAL --- */
export default function DetalhesEmpreendimento() {
  const { id } = useParams();
  const [dados, setDados] = useState<Empreendimento | null>(null);
  const [torreSelecionada, setTorreSelecionada] = useState<string | null>(null);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState<Unidade | null>(null);
  const [abaSelecionada, setAbaSelecionada] = useState<string>(
    dados?.unidadesAvulsas.length ? "avulsas" : dados?.torres[0]?.nome || ""
  );
  const acessos = JSON.parse(localStorage.getItem("acessos") || "[]");
  const podeVerTabela = acessos.includes("TABELAS_PRECIFICACAO");

  useEffect(() => {
    if (!id) return;

    const fetchEmpreendimento = async () => {
      try {
        const { data } = await api.get(`/tela empreendimento/${id}`);
        const { data: unidadesAPI } = await api.get(`/unidades-por-empreendimento/?empreendimento_id=${id}`);

        const unidades: Unidade[] = unidadesAPI.map((u: any) => ({
          id: u.id.toString(),
          numero: u.numero_apartamento.toString(),
          valor: u.valor_imovel,
          area: parseFloat(u.area_privativa_m2),
          vagas: u.quantidade_garagem,
          andar: u.andar,
          status: u.disponibilidade === 'Disponível' ? 1 : 2,
          torre: u.torre,
          atributosExtras: u.atributos_extras
            ? Object.entries(u.atributos_extras).map(([nome, valor]) => ({ nome, valor }))
            : [],
        }));

        // Agrupar por torre
        const torresMap: Record<string, Unidade[]> = {};
        const avulsas: Unidade[] = [];
        const torresArray: Torre[] = [];

        // Preenche o mapa de torres
        unidades.forEach((u: Unidade) => {
          const torreNome = u.torre?.toString() || "0";
          if (!torresMap[torreNome]) {
            torresMap[torreNome] = [];
          }
          torresMap[torreNome].push(u);
        });

        // Transforma em torres e avulsas
        Object.entries(torresMap).forEach(([nome, unidadesPorAndar]: [string, Unidade[]]) => {
          if (nome === "0") {
            // Torre 0 => unidade avulsa
            avulsas.push(...unidadesPorAndar);
          } else {
            // Normaliza andares (1 = térreo, 2 = andar 1, etc.)
            const unidadesNormalizadas: Unidade[] = unidadesPorAndar.map((u: Unidade) => ({
              ...u,
              andar: u.andar,
            }));

            const maxAndar = Math.max(1, ...unidadesNormalizadas.map((u: Unidade) => u.andar));

            torresArray.push({
              nome,
              andares: maxAndar,
              unidadesPorAndar: unidadesNormalizadas,
            });
          }
        });



        const transformado: Empreendimento = {
          id: data.id.toString(),
          titulo: data.nome,
          bairro: data.endereco.bairro || "",
          cidade: data.endereco.municipio?.toString() || "",
          preco: "",
          fotos: Array.isArray(data.imagens) && data.imagens.length > 0
            ? data.imagens.map((img: string) => img)
            : ["/placeholder.jpg"],
          area: data.area_total,
          vagas: 0,
          caracteristicas: {
            lazer: data.itens_empreendimento?.lazer || [],
            seguranca: data.itens_empreendimento?.seguranca || [],
            infraestrutura: data.itens_empreendimento?.infraestrutura || [],
          },
          construtora: data.construtora || "",
          unidadesDisponiveis: data.unidades_disponiveis || 0,
          faseObra: data.fase_empreendimento || "",
          valorMedio: data.valor_medio?.toString() || "",
          torres: torresArray,
          unidadesAvulsas: avulsas,
          latitude: parseFloat(data.endereco.latitude || "0"),
          longitude: parseFloat(data.endereco.longitude || "0"),
          tabela_preco: data.tabela_preco?.toString() || "",
        };

        setDados(transformado);

      } catch (err) {
        console.error("Erro ao buscar empreendimento ou unidades", err);
        setDados(null);
      }
    };

    fetchEmpreendimento();
  }, [id]);

  if (!dados) return <Loading />;

  const statusMap: Record<Unidade["status"], { label: string; color: string }> = {
    1: { label: "Disponível", color: "#44c3ff" },
    2: { label: "Vendido", color: "#ff4d4f" },
  };

  const handleDownloadPdf = () => {
    if (!dados?.tabela_preco) {
      alert("Nenhum relatório disponível para visualização.");
      return;
    }
    window.open(dados.tabela_preco, "_blank");
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ padding: 20, background: '#f5f5f5', minHeight: '100vh', flex: 1 }}>
        <div className="card-maior">
          <div className="detalhes-container">
            {/* Header */}
            <div className="header">
              <div>
                <h1 className="titulo">{dados.titulo}</h1>
                <p className="localizacao">{dados.bairro}, {dados.cidade}</p>
              </div>
            </div>

            {/* Galeria */}
            <div className="galeria">
              <Slider
                dots={true}
                infinite={true}
                speed={500}
                slidesToShow={1}
                slidesToScroll={1}
                className="slider"
                arrows={true}
                adaptiveHeight={true} // ajusta a altura de acordo com a imagem
              >
                {dados.fotos.map((foto, idx) => (
                  <div key={idx}>
                    <img src={foto} alt={`Foto ${idx + 1}`} className="foto-slider" />
                  </div>
                ))}
              </Slider>
            </div>

            {/* Sobre o Empreendimento */}
            <h2 className="subtitulo">Sobre o empreendimento</h2>
            <div className="sobre-empreendimento">
              <div className="sobre-card">
                <Briefcase size={24} color="#29306a" />
                <div>
                  <strong>Construtora</strong>
                  <p>{dados.construtora}</p>
                </div>
              </div>
              <div className="sobre-card">
                <Home size={24} color="#29306a" />
                <div>
                  <strong>Unidades disponíveis</strong>
                  <p>{dados.unidadesDisponiveis}</p>
                </div>
              </div>
              <div className="sobre-card">
                <Layers size={24} color="#29306a" />
                <div>
                  <strong>Etapa</strong>
                  <p>{dados.faseObra}</p>
                </div>
              </div>
              <div className="sobre-card">
                <Ruler size={24} color="#29306a" />
                <div>
                  <strong>Valor médio</strong>
                  <p>
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(Number(dados.valorMedio))}
                  </p>
                </div>
              </div>
            </div>

            {podeVerTabela && (
              <button onClick={handleDownloadPdf} className="botao-tabela">
                <Download size={16} />
                Baixar tabela em PDF
              </button>
            )}

            <div className="abas-torres">
              {/* Botões de seleção de aba */}
              <div className="botoes-abas">
                {dados.unidadesAvulsas.length > 0 && (
                  <button
                    className={abaSelecionada === "avulsas" ? "active" : ""}
                    onClick={() => setAbaSelecionada("avulsas")}
                  >
                    Avulsas
                  </button>
                )}
                {dados.torres.map(t => (
                  <button
                    key={t.nome}
                    className={abaSelecionada === t.nome ? "active" : ""}
                    onClick={() => setAbaSelecionada(t.nome)}
                  >
                    Torre {t.nome}
                  </button>
                ))}
              </div>

              {/* Matriz de Unidades */}
              {abaSelecionada && (
                <div className="unidades-por-aba">
                  {abaSelecionada === "avulsas" && (
                    <div className="lista-avulsas">
                      {dados.unidadesAvulsas.map(u => (
                        <button
                          key={u.id}
                          className={`unidade-btn ${unidadeSelecionada?.id === u.id ? "active" : ""}`}
                          style={{ backgroundColor: statusMap[u.status].color }}
                          onClick={() => setUnidadeSelecionada(u)}
                        >
                          {u.numero}
                        </button>
                      ))}
                    </div>
                  )}

                  {dados.torres
                    .filter(torre => torre.nome === abaSelecionada)
                    .map(torre => (
                      <div key={torre.nome} className="matriz-por-andar">
                        {Array.from({ length: torre.andares }, (_, i) => torre.andares - i).map(andar => (
                          <div key={andar} className="andar-linha">
                            <div className="andar-nome">
                              {andar === 1 ? "Térreo" : `Andar ${andar - 1}`}
                            </div>
                            <div className="unidades-andar">
                              {torre.unidadesPorAndar
                                .filter(u => u.andar === andar)
                                .map(u => (
                                  <button
                                    key={u.id}
                                    className={`unidade-btn ${unidadeSelecionada?.id === u.id ? "active" : ""}`}
                                    style={{ backgroundColor: statusMap[u.status].color }}
                                    onClick={() => setUnidadeSelecionada(u)}
                                  >
                                    {u.numero}
                                  </button>
                                ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Características */}
            <h2 className="subtitulo">Características</h2>
            <div className="caracteristicas">
              <div className="caracteristica-box scroll-box">
                <TreePalm size={32} color="#29306a" />
                <h3>Lazer</h3>
                <ul>{dados.caracteristicas.lazer.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
              </div>
              <div className="caracteristica-box scroll-box">
                <ShieldCheck size={32} color="#29306a" />
                <h3>Segurança</h3>
                <ul>{dados.caracteristicas.seguranca.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
              </div>
              <div className="caracteristica-box scroll-box">
                <Building2 size={32} color="#29306a" />
                <h3>Infraestrutura</h3>
                <ul>{dados.caracteristicas.infraestrutura.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
              </div>
            </div>

            {/* Mapa Mapbox */}
            <div className="mapa">
              <h2 className="subtitulo">Mapa</h2>
              <Map
                initialViewState={{
                  longitude: dados.longitude,
                  latitude: dados.latitude,
                  zoom: 15,
                }}
                style={{ width: "100%", height: "300px" }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                mapboxAccessToken="pk.eyJ1IjoiZXZhbmRzb25jZXNhciIsImEiOiJjbWN3YXNtMjkwMGl2Mm5wd21mdGRlazA0In0.bvgSecEoO0w-PDKVfkcpjQ"
              >
                <Marker longitude={dados.longitude} latitude={dados.latitude} color="red" />
              </Map>
            </div>
          </div>
        </div>

        {/* Modal */}
        <UnidadeModal unidade={unidadeSelecionada} onClose={() => setUnidadeSelecionada(null)} />
      </div>
    </div>
  );
}
